import React from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * Catches render / lazy-import failures so the app shows a recovery UI
 * instead of a blank screen. User-facing copy stays short — no stack traces.
 */
export default class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, offline: false };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            offline: typeof navigator !== 'undefined' && !navigator.onLine,
            message: error?.message || String(error || ''),
        };
    }

    componentDidCatch(error, info) {
        console.error('AppErrorBoundary:', error, info?.componentStack);
        const message = error?.message || String(error || '');
        this.setState({ message });
        // Stale webpack chunks after a hot rebuild: recover with one hard reload
        // before the user ever sees the fallback UI.
        if (typeof window !== 'undefined' && /Loading chunk|ChunkLoadError|Unexpected token|Failed to fetch dynamically imported module/i.test(message)) {
            const key = 'tauris-chunk-reload';
            if (!sessionStorage.getItem(key)) {
                sessionStorage.setItem(key, '1');
                window.location.reload();
            }
        }
    }

    handleRetry = () => {
        const message = this.state.message || '';
        this.setState({ hasError: false, offline: false, message: '' });
        if (typeof window !== 'undefined') {
            if (/Loading chunk|ChunkLoadError|Unexpected token|Failed to fetch dynamically imported module/i.test(message)) {
                const key = 'tauris-chunk-reload';
                if (!sessionStorage.getItem(key)) {
                    sessionStorage.setItem(key, '1');
                    window.location.reload();
                    return;
                }
                sessionStorage.removeItem(key);
            } else {
                try { sessionStorage.removeItem('tauris-chunk-reload'); } catch { /* ignore */ }
            }
            window.location.reload();
        }
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <Box
                sx={{
                    minHeight: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    px: 3,
                    textAlign: 'center',
                    bgcolor: '#fcfefe',
                }}
            >
                <Typography variant="h6" fontWeight={800} color="#383b50">
                    {this.state.offline ? 'You are offline' : 'This page could not be loaded'}
                </Typography>
                <Typography variant="body2" color="#6b7280" sx={{ maxWidth: 420 }}>
                    {this.state.offline
                        ? 'Reconnect to the internet, then reload this page.'
                        : 'Please reload the page. If the problem continues, sign out and sign back in.'}
                </Typography>
                <Button
                    variant="contained"
                    onClick={this.handleRetry}
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#09b8a9', '&:hover': { bgcolor: '#06857a' } }}
                >
                    Reload
                </Button>
            </Box>
        );
    }
}
